import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import cn from 'classnames';
import * as motion from 'motion/react-client';

import { Theme, useTheme } from '@/services/theme';
import { breakpoints } from '@/shared/styles';
import { useBreakpointDown } from '@/shared/utils';
import { zeroAddress } from '@/features/vaults/config';
import { TokenIcon } from '@/shared/components/TokenIcon';
import { Network } from '@/shared/types/Network';

import BgBlur from './bg_blur.svg';

const tokenWidth = {
  sm: 60,
  md: 150,
} as const;

const gap = {
  sm: 4,
  md: 16,
} as const;

const Container = styled.section`
  position: relative;
  text-align: center;
  padding-bottom: 60px;
  background: rgba(16, 17, 21, 1);

  @media (min-width: ${breakpoints.tabletSM}px) {
    padding-bottom: 180px;
  }

  .Content__border_top {
    background: rgba(16, 17, 21, 1);
    height: 60px;
    width: 100%;
    border-radius: 64px 64px 0 0;
  }

  h2 {
    padding-top: 0px;
    background: rgba(16, 17, 21, 1);

    @media (min-width: ${breakpoints.tabletSM}px) {
      padding-top: 120px;
    }
  }

  .bg__blur_blue {
    position: absolute;
    width: 324px;
    height: 325px;
    bottom: -150px;
    left: 0px;

    background-image: url(${BgBlur});
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center;
    overflow: hidden;
    z-index: 0;

    @media (min-width: ${breakpoints.tabletSM}px) {
      width: 687px;
      height: 610px;
      bottom: -300px;
      left: 0px;
    }
  }
`;

const StyledContent = styled.div`
  background: rgba(16, 17, 21, 1);

  font-family: Gramatika;

  .SupportedTokens {
    &__description {
      color: ${({ theme }: { theme: Theme }) => theme.colors.text.description};
      line-height: 28.8px;
      font-size: 18px;
      @media (min-width: ${breakpoints.tabletLG}px) {
        font-size: 20px;
      }
    }

    &__list {
      display: flex;
      justify-content: center;
      flex-wrap: wrap;
      gap: 8px ${gap.sm}px;
      margin-top: 36px;
      @media (min-width: ${breakpoints.tabletSM}px) {
        gap: ${gap.md}px;
      }
      @media (min-width: ${breakpoints.tabletLG}px) {
        margin-top: 51px;
      }
    }

    &__token-wrapper {
      position: relative;
    }

    &__token {
      border-radius: 50px;
      background: rgba(255, 255, 255, 1);
      border: 1px solid #2d2e39;
      font-size: 16px;
      display: flex;
      align-items: center;
      gap: 4px;
      color: rgba(16, 17, 21, 1);

      /* width: ${tokenWidth.sm}px; */
      height: 40px;
      padding: 6px 8px 6px;

      &_disabled {
        svg {
          filter: grayscale(100%);
        }
        opacity: 0.6;
      }

      @media (min-width: ${breakpoints.tabletSM}px) {
        /* width: ${tokenWidth.md}px; */
        height: 64px;
        padding: 6px 10px 6px;
        gap: ${gap.md}px;
        padding: 11px 26px 11px 12px;
      }
    }

    &__token_first {
      border-radius: 50px;
      background: linear-gradient(270deg, #24262f 0%, rgba(36, 38, 47, 0) 100%);

      width: ${tokenWidth.sm}px;
      height: 40px;

      @media (min-width: ${breakpoints.tabletSM}px) {
        width: ${tokenWidth.md}px;
        height: 64px;
      }
    }

    &__token_last {
      border-radius: 50px;
      background: linear-gradient(270deg, rgba(36, 38, 47, 0) 0%, #24262f 100%);

      width: ${tokenWidth.sm}px;
      height: 40px;

      @media (min-width: ${breakpoints.tabletSM}px) {
        width: ${tokenWidth.md}px;
        height: 64px;
      }
    }

    &__icon {
      display: flex;
      align-items: center;
    }

    &__title {
      font-size: 16px;

      @media (min-width: ${breakpoints.tabletLG}px) {
        font-size: 21px;
      }
    }

    &__soon-badge {
      position: absolute;
      top: -21px;
      right: -9px;
      border-radius: 50px;
      background: #ec0b38;
      font-weight: 700;
      border: 5px solid ${({ theme }: { theme: Theme }) => theme.colors.pageBg};
      font-size: 10px;
      text-transform: uppercase;
      right: -16px;
      padding: 2px 7px 4px;
      @media (min-width: ${breakpoints.tabletLG}px) {
        padding: 5px 10px 7px;
        right: -9px;
      }
    }
  }
`;

const YourERC20 = ({ isTablet }: { isTablet: boolean }) => (
  <div className="SupportedTokens__token your_erc20">
    <div className="SupportedTokens__icon">
      <motion.div
        animate={{
          background: [
            'rgba(14, 183, 82, 1)',
            'rgba(94, 91, 220, 1)',
            'rgba(225, 78, 113, 1)',
            'rgba(239, 168, 45, 1)',
            'rgba(14, 183, 82, 1)',
          ],
        }}
        transition={{
          duration: 3,
          ease: 'easeInOut',
          times: [0, 0.25, 0.5, 0.75, 1],
          repeat: Infinity,
          repeatDelay: 1,
        }}
        style={{
          width: isTablet ? '24px' : '40px',
          height: isTablet ? '24px' : '40px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <motion.div
          animate={{
            rotate: [0, 0, 180, 180, 0],
            borderRadius: ['10%', '50%', '10%', '50%', '10%'],
          }}
          transition={{
            duration: 3,
            ease: 'easeInOut',
            times: [0, 0.25, 0.5, 0.75, 1],
            repeat: Infinity,
            repeatDelay: 1,
          }}
          style={{
            width: isTablet ? '8px' : '12px',
            height: isTablet ? '8px' : '12px',
            borderRadius: '50%',
            background: '#fff',
          }}
        />
      </motion.div>
    </div>
    <div className="SupportedTokens__title">Your ERC20</div>
  </div>
);

const tokens: { chain: Network; address: string; symbol: string; soon: boolean }[] = [
  {
    chain: 'ethereum',
    address: '0xae7ab96520DE3A18E5e111B5EaAb095312D7fE84',
    symbol: 'stETH',
    soon: false,
  },
  { chain: 'ethereum', address: zeroAddress, symbol: 'ETH', soon: false },
  {
    chain: 'ethereum',
    address: '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2',
    symbol: 'wETH',
    soon: false,
  },
  {
    chain: 'ethereum',
    address: '0x7f39C581F595B53c5cb19bD0b3f8dA6c935E2Ca0',
    symbol: 'wstETH',
    soon: false,
  },
  {
    chain: 'ethereum',
    address: '0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599',
    symbol: 'WBTC',
    soon: false,
  },
  {
    chain: 'ethereum',
    address: '0x18084fbA666a33d37592fA2633fD49a74DD93a88',
    symbol: 'tBTC',
    soon: false,
  },
  {
    chain: 'ethereum',
    address: '0x9D39A5DE30e57443BfF2A8307A4256c8797A3497',
    symbol: 'sUSDe',
    soon: false,
  },
  {
    chain: 'ethereum',
    address: '0x57e114B691Db790C35207b2e685D4A43181e6061',
    symbol: 'ENA',
    soon: false,
  },
];

/**
 * Add "first" and "last" elements ("borders") in a flex-wrap environment.
 * @param list - The wrapper element containing the tokens.
 */
const flexRowFirstLast = (list: HTMLDivElement) => {
  const tokens = Array.from(
    list.getElementsByClassName('SupportedTokens__token'),
  ) as HTMLDivElement[];
  const tokensFirst = Array.from(
    list.getElementsByClassName('SupportedTokens__token_first'),
  ) as HTMLDivElement[];
  const tokensLast = Array.from(
    list.getElementsByClassName('SupportedTokens__token_last'),
  ) as HTMLDivElement[];

  const size = window.innerWidth > breakpoints.tabletSM ? 'md' : 'sm';

  // Cleanup first and last tokens
  tokensFirst.forEach(token => {
    list.removeChild(token);
  });

  tokensLast.forEach(token => {
    list.removeChild(token);
  });

  let tokenIdx = 0;
  let offsetWidth = 0;
  let firstToken: HTMLDivElement | null = null;

  while (offsetWidth < list.offsetWidth && tokenIdx < tokens.length) {
    // first
    if (offsetWidth === 0) {
      offsetWidth += tokenWidth[size] + gap[size];
      if (tokens[tokenIdx]) {
        firstToken = document.createElement('div');
        firstToken.className = 'SupportedTokens__token_first';
        list.insertBefore(firstToken, tokens[tokenIdx]);
      }
    }

    const nextTokenWidth = tokens[tokenIdx]?.offsetWidth || tokenWidth[size];

    // not enough room to fit the next token + gap + border
    if (offsetWidth + nextTokenWidth + 2 * gap[size] + tokenWidth[size] > list.offsetWidth) {
      if (tokens[tokenIdx]) {
        const lastToken = document.createElement('div');
        lastToken.className = 'SupportedTokens__token_last';

        // enough room to fit 2 borders -> force line break to ensure there's no 2 successive borders on the same row
        if (offsetWidth + 2 * gap[size] + 2 * tokenWidth[size] <= list.offsetWidth) {
          if (firstToken) {
            firstToken.style.width = `${1.5 * tokenWidth[size]}px`;
          }
          lastToken.style.width = `${1.5 * tokenWidth[size]}px`;
        }

        list.insertBefore(lastToken, tokens[tokenIdx]);
        firstToken = null;
      }
      offsetWidth = 0;
    } else {
      offsetWidth += nextTokenWidth + gap[size];
      tokenIdx += 1;
    }
  }

  const lastToken = document.createElement('div');
  lastToken.className = 'SupportedTokens__token_last';
  list.appendChild(lastToken);
};

export const SupportedTokens = () => {
  const listWrapperRef = useRef<HTMLDivElement>(null);
  const { theme } = useTheme();
  const isTablet = useBreakpointDown(breakpoints.tabletLG);

  useEffect(() => {
    if (listWrapperRef.current) {
      flexRowFirstLast(listWrapperRef.current);
    }
  }, [listWrapperRef]);

  useEffect(() => {
    const handleResize = () => {
      if (listWrapperRef.current) {
        flexRowFirstLast(listWrapperRef.current);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [listWrapperRef]);

  return (
    <Container>
      <div className="Content__border_top" />
      <div className="bg__blur_blue" />

      <h2>Asset-agnostic</h2>
      <StyledContent theme={theme}>
        <div className="Content__description2">
          Mellow supports any and all assets required by our partners.
        </div>

        <div ref={listWrapperRef} className="SupportedTokens__list">
          {tokens.map(({ chain, address, symbol, soon }, idx) => (
            <div
              key={`token_${idx}`}
              className={cn('SupportedTokens__token', {
                // eslint-disable-next-line camelcase
                SupportedTokens__token_disabled: soon,
              })}
            >
              <div className="SupportedTokens__icon">
                <TokenIcon
                  size={isTablet ? 'sm' : 'md'}
                  chainAddressOrSymbol={`${chain}:${address}`}
                />
              </div>
              <div className="SupportedTokens__title">{symbol}</div>
            </div>
          ))}
          <YourERC20 isTablet={isTablet} />
        </div>
      </StyledContent>
    </Container>
  );
};
